/**
 * MyActions — personal to-do list.
 *
 * Emma's ask (Head of Recoveries, Jul 23 2026): "an action list I can run
 * to see everything assigned to me and the date they are due." Aggregates
 * Complaints + Legal Cases + Collections Cases into one sortable view
 * with badges for overdue / due today.
 *
 * Emma-friendly features:
 *   • Overdue items at the top, colour-coded red.
 *   • Filter chips per source (all / complaints / legal / collections).
 *   • Search across title/subtitle so she can jump to a specific customer.
 *   • One-click deep-link to the underlying case.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ListChecks, AlertCircle, Clock, Calendar, Search, RefreshCw,
  Megaphone, Scale, PiggyBank, ArrowUpRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const SOURCE_META = {
  complaint:   { label: "Complaints",  icon: Megaphone, tint: "text-rose-600 bg-rose-50 border-rose-200" },
  legal:       { label: "Legal",       icon: Scale,     tint: "text-indigo-600 bg-indigo-50 border-indigo-200" },
  collections: { label: "Collections", icon: PiggyBank, tint: "text-amber-600 bg-amber-50 border-amber-200" },
};

function formatDue(iso) {
  if (!iso) return { label: "No date", tone: "text-slate-500" };
  const today = new Date().toISOString().slice(0, 10);
  if (iso < today) {
    const daysAgo = Math.floor((new Date(today) - new Date(iso)) / 86_400_000);
    return { label: daysAgo === 1 ? "1 day overdue" : `${daysAgo} days overdue`, tone: "text-red-600 font-semibold" };
  }
  if (iso === today) return { label: "Due today", tone: "text-amber-600 font-semibold" };
  const daysAhead = Math.floor((new Date(iso) - new Date(today)) / 86_400_000);
  if (daysAhead <= 7) return { label: `Due in ${daysAhead} day${daysAhead === 1 ? "" : "s"}`, tone: "text-slate-700" };
  const d = new Date(iso);
  return { label: d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }), tone: "text-slate-500" };
}

const SOURCE_FILTERS = ["all", "complaint", "legal", "collections"];

export default function MyActions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async (silent = false) => {
    if (!user?.email) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get(`/my-actions?email=${encodeURIComponent(user.email)}`);
      setData(res.data);
    } catch (e) {
      console.error("Failed to load my actions", e);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [user?.email]);

  const filtered = useMemo(() => {
    if (!data?.actions) return [];
    const q = search.trim().toLowerCase();
    return data.actions.filter((a) => {
      if (sourceFilter !== "all" && a.source !== sourceFilter) return false;
      if (!q) return true;
      return (a.title || "").toLowerCase().includes(q)
        || (a.subtitle || "").toLowerCase().includes(q);
    });
  }, [data, sourceFilter, search]);

  const counts = data?.counts || { total: 0, overdue: 0, due_today: 0, future: 0, undated: 0, by_source: {} };

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-6" data-testid="my-actions-page">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <ListChecks className="w-4 h-4" />
            <span>My Work</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
            My Actions
          </h1>
          <p className="text-slate-500 mt-1">
            Everything currently assigned to you across the portal, sorted by due date.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing} data-testid="my-actions-refresh">
          <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={AlertCircle} label="Overdue"   value={counts.overdue}   tone="text-red-700 bg-red-50 border-red-200" testId="summary-overdue" />
        <SummaryCard icon={Clock}       label="Due today" value={counts.due_today} tone="text-amber-700 bg-amber-50 border-amber-200" testId="summary-due-today" />
        <SummaryCard icon={Calendar}    label="Upcoming"  value={counts.future}    tone="text-blue-700 bg-blue-50 border-blue-200" testId="summary-upcoming" />
        <SummaryCard icon={ListChecks}  label="Total"     value={counts.total}     tone="text-slate-700 bg-slate-50 border-slate-200" testId="summary-total" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex flex-wrap gap-2">
            {SOURCE_FILTERS.map((s) => {
              const active = sourceFilter === s;
              const count = s === "all" ? counts.total : (counts.by_source?.[s] || 0);
              const meta = SOURCE_META[s];
              return (
                <button
                  key={s}
                  onClick={() => setSourceFilter(s)}
                  className={cn(
                    "px-3 py-1.5 text-sm rounded-full border transition-colors flex items-center gap-2",
                    active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  )}
                  data-testid={`filter-${s}`}
                >
                  {meta?.icon ? <meta.icon className="w-3.5 h-3.5" /> : null}
                  {s === "all" ? "All" : SOURCE_META[s]?.label}
                  <span className={cn("text-xs rounded-full px-1.5 py-0.5", active ? "bg-white/20" : "bg-slate-100")}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by customer, topic or SF number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="my-actions-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-500">
            <ListChecks className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">Nothing assigned to you right now</p>
            <p className="text-sm mt-1">
              {sourceFilter !== "all" || search
                ? "Try removing filters or the search term."
                : "You're all caught up. When a complaint, legal case, or arrears case is assigned to you it'll appear here."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => <ActionRow key={a.id} action={a} onOpen={() => navigate(a.deep_link)} />)}
        </div>
      )}
    </div>
  );
}

const SummaryCard = ({ icon: Icon, label, value, tone, testId }) => (
  <Card className={cn("border", tone)} data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="text-3xl font-bold">{value}</div>
    </CardContent>
  </Card>
);

const ActionRow = ({ action, onOpen }) => {
  const meta = SOURCE_META[action.source] || SOURCE_META.legal;
  const dueDisplay = formatDue(action.due_date);
  return (
    <button
      onClick={onOpen}
      className={cn(
        "w-full text-left bg-white border rounded-lg p-4 hover:shadow-md hover:border-slate-300 transition-all",
        "flex items-center gap-4 group",
        action.overdue && "border-l-4 border-l-red-500"
      )}
      data-testid={`action-row-${action.id}`}
    >
      <div className={cn("shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border", meta.tint)}>
        <meta.icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {meta.label}
          </span>
          {action.overdue && (
            <Badge variant="destructive" className="text-xs h-5" data-testid="badge-overdue">Overdue</Badge>
          )}
          {action.due_today && !action.overdue && (
            <Badge className="text-xs h-5 bg-amber-500 hover:bg-amber-600">Due today</Badge>
          )}
        </div>
        <div className="font-medium text-slate-900 truncate">{action.title}</div>
        <div className="text-sm text-slate-500 mt-0.5 truncate">{action.subtitle}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className={cn("text-sm", dueDisplay.tone)}>{dueDisplay.label}</div>
        <div className="text-xs text-slate-400 mt-0.5 flex items-center justify-end gap-1 group-hover:text-primary transition-colors">
          Open <ArrowUpRight className="w-3 h-3" />
        </div>
      </div>
    </button>
  );
};
